import api from './api';

// Tipos para las respuestas de la API
interface ListarClientesResponse {
  success: boolean;
  data: Cliente[];
  total: number;
}

interface Cliente {
  id: number;
  nombre: string;
  rut: string;
  email: string;
  nombre_contacto: string;
  telefono_contacto?: string;
  tipo_cliente: 'persona_natural' | 'juridica';
  razon_social?: string;
  representante_legal?: string;
  observaciones_despacho?: string;
  usuario_id?: number;
  activo: boolean;
  created_at: string;
  updated_at: string;
}

interface CreateClienteRequest {
  nombre: string;
  rut: string;
  email: string;
  nombre_contacto: string;
  telefono_contacto?: string;
  tipo_cliente: 'persona_natural' | 'juridica';
  razon_social?: string;
  representante_legal?: string;
  observaciones_despacho?: string;
  // Vinculo con el usuario que accede al portal. Reemplaza al viejo
  // `crear_usuario`, que el backend validaba y nunca usaba.
  usuario_id?: number | null;
  activo?: boolean;
}

interface UpdateClienteRequest {
  nombre?: string;
  rut?: string;
  email?: string;
  nombre_contacto?: string;
  telefono_contacto?: string;
  tipo_cliente?: 'persona_natural' | 'juridica';
  razon_social?: string;
  representante_legal?: string;
  observaciones_despacho?: string;
  activo?: boolean;
}

class ClientesAPI {
  // Listar clientes
  async listar(params?: {
    search?: string;
    tipo_cliente?: 'persona_natural' | 'juridica';
    activo?: boolean;
    limit?: number;
    page?: number;
  }): Promise<ListarClientesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.tipo_cliente)
      searchParams.append('tipo_cliente', params.tipo_cliente);
    if (params?.activo !== undefined)
      searchParams.append('activo', params.activo.toString());
    if (params?.limit !== undefined)
      searchParams.append('limit', String(params.limit));
    if (params?.page !== undefined)
      searchParams.append('page', String(params.page));

    const response = await api.get(`/clientes?${searchParams.toString()}`);
    return response.data;
  }

  // Obtener cliente por ID
  async obtener(id: number): Promise<{ success: boolean; data: Cliente }> {
    const response = await api.get(`/clientes/${id}`);
    return response.data;
  }

  /**
   * Ficha del cliente vinculado al usuario logueado.
   *
   * Es la vía para el rol `cliente`, que no tiene permiso para leer el módulo
   * y por lo tanto no puede usar `obtener(id)`. Resuelve en el backend por
   * `clientes.usuario_id`; devuelve 404 si el usuario no está vinculado.
   */
  async miFicha(): Promise<{ success: boolean; data: Cliente }> {
    const response = await api.get('/clientes/mi-ficha');
    return response.data;
  }

  /**
   * Usuarios de rol cliente que todavía no tienen ficha asociada.
   *
   * Alimenta el selector del wizard. Al editar hay que pasar el id de la ficha
   * para que su usuario actual venga incluido: si no, quedaría filtrado por
   * tener ficha y el selector saldría vacío sobre un cliente vinculado.
   */
  async usuariosVinculables(
    clienteId?: number
  ): Promise<{
    success: boolean;
    data: Array<{ id: number; nombre: string; email: string }>;
  }> {
    const response = await api.get('/clientes/usuarios-vinculables', {
      params: clienteId ? { cliente_id: clienteId } : undefined,
    });
    return response.data;
  }

  // Crear cliente
  async crear(
    data: CreateClienteRequest
  ): Promise<{ success: boolean; data: Cliente; message: string }> {
    const response = await api.post('/clientes', data);
    return response.data;
  }

  // Actualizar cliente
  async actualizar(
    id: number,
    data: UpdateClienteRequest
  ): Promise<{ success: boolean; data: Cliente; message: string }> {
    const response = await api.put(`/clientes/${id}`, data);
    return response.data;
  }

  // Eliminar (desactivar) cliente
  async eliminar(
    id: number
  ): Promise<{ success: boolean; data: Cliente; message: string }> {
    const response = await api.delete(`/clientes/${id}`);
    return response.data;
  }
}

export const clientesAPI = new ClientesAPI();
export type { Cliente, CreateClienteRequest, UpdateClienteRequest };
